import jsonExport from "jsonexport/dist";
import { downloadCSV } from "react-admin";
import { formatTimestamp } from "../../../lib/dateFormat";

const statusLabel = {
  submitted: "Submitted",
  complete: "In Connect",
};

const formatUser = (user) => {
  if (!user) {
    return "";
  }
  return `${user.firstName} ${user.lastName} (${user.email})`;
};

// transformContractRequest takes a contract request and breaks it out into individual contracts
function transformContractRequest(contractRequest, carriers, agencies, users, specialists) {
  const {
    id,
    npn,
    firstName,
    lastName,
    preferredName,
    phone,
    email,
    address,
    city,
    state,
    zip,
    upline,
    topOfHierarchy,
    payment,
    paymentNpn,
    paymentCorporation,
    paymentTaxId,
    paymentUpline,
    notes,
    user: userId,
    specialist: specialistId,
    agent,
    agency,
    emailRecipients,
    status,
    unknownAgent,
    commissionEmail,
    enrollmentEmail,
    createdAt,
    updatedAt,
  } = contractRequest;
  const emailRecipientList = emailRecipients.join("; ");
  const user = users[userId];
  const specialist = specialists[specialistId]
  const userDescription = user ? `${user.firstName} ${user.lastName} (${user.email})` : "Unknown";
  const unknownAgentText = (unknownAgent) ? "Yes" : "No";

  return contractRequest.contracts.map((contract) => {
    const carrierStatesText = contract.carrierStates ? contract.carrierStates.join(", ") : "";
    const withReleaseText = contract.withRelease ? "Yes" : "No";

    return {
      "Contract Request ID": id,
      "Carrier Name": carriers[contract.carrier]?.value || "Unknown",
      "Agent NPN": npn,
      "Agent First Name": firstName,
      "Agent Last Name": lastName,
      "Agent Preferred Name": preferredName,
      "Agent Email": email,
      "Agent Phone": phone,
      "Agent Address": address,
      "Agent City": city,
      "Agent State": state,
      "Agent ZIP": zip,
      "Agent Upline": upline,
      "Agent Top of Hierarchy": topOfHierarchy,
      "Agent in database": agent ? "Yes" : "No",
      "Unknown Agent": unknownAgentText,
      "Payment Option": payment,
      "Payment NPN": paymentNpn,
      "Payment Corporation": paymentCorporation,
      "Payment Tax ID": paymentTaxId,
      "Payment Upline": paymentUpline,
      "Commission Email": commissionEmail,
      "Enrollment Email": enrollmentEmail,
      "Policy Entity": contract.policyEntity,
      "With Release": withReleaseText,
      "Policy Creation": contract.policyCreation,
      "Carrier States": carrierStatesText,
      "Carrier Type": contract.carrierType,
      "Carrier Level": contract.carrierLevel,
      "Contract Notes": contract.notes,
      "Contract Status": statusLabel[contract.status],
      "Overall Status": status,
      "COB": formatUser(specialist),
      "Agency": agencies[agency]?.name || "N/A",
      "PURL": agencies[agency]?.domain || "N/A",
      "Submitted By": userDescription,
      "Email Recipients": emailRecipientList,
      "Request Notes": notes,
      "Request Created": formatTimestamp(createdAt),
      "Request Updated": formatTimestamp(updatedAt),
      "Contract Created": formatTimestamp(contract.createdAt),
      "Contract Updated": formatTimestamp(contract.updatedAt),
    };
  });
}

const headers = [
  "Contract Request ID",
  "Carrier Name",
  "Agent NPN",
  "Agent First Name",
  "Agent Last Name",
  "Agent Preferred Name",
  "Agent Email",
  "Agent Phone",
  "Agent Address",
  "Agent City",
  "Agent State",
  "Agent ZIP",
  "Agent Upline",
  "Agent Top of Hierarchy",
  "Agent in database",
  "Unknown Agent",
  "Payment Option",
  "Payment NPN",
  "Payment Corporation",
  "Payment Tax ID",
  "Payment Upline",
  "Commission Email",
  "Enrollment Email",
  "Policy Entity",
  "With Release",
  "Policy Creation",
  "Carrier States",
  "Carrier Type",
  "Carrier Level",
  "Contract Notes",
  "Contract Status",
  "Overall Status",
  "COB",
  "Agency",
  "PURL",
  "Submitted By",
  "Email Recipients",
  "Request Notes",
  "Request Created",
  "Request Updated",
  "Contract Created",
  "Contract Updated",
];

const exporter = async (records, fetchRelatedRecords) => {
  const agencies = await fetchRelatedRecords(records, "agency", "agency");
  const contracts = records.map((cr) => cr.contracts);
  const carriers = await fetchRelatedRecords(contracts, "carrier", "carrier");
  const users = await fetchRelatedRecords(contracts, "user", "user");
  const specialists = await fetchRelatedRecords(contracts, "specialist", "user");

  const data = records.map((cr) =>
    transformContractRequest(cr, carriers, agencies, users, specialists)
  );
  const report = await jsonExport(data, { headers });
  return downloadCSV(report, "Contract Requests");
};

export default exporter;
